import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';

const CREATE_USER_REPORT = gql`
  mutation CreateUserReport($input: CreateUserReportInput!) {
    createUserReport(input: $input) {
      id
      reason
      evidence
      images
      status
      createdAt
    }
  }
`;

const GET_PRESIGNED_URL_FOR_REPORT = gql`
  mutation GetPresignedUrlForReport($fileType: String!) {
    getPresignedUrlForReport(fileType: $fileType) {
      url
      key
    }
  }
`;

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportedUserId: string;
  reportedUserName: string;
  bookingId?: string;
  chatId?: string;
}

const Overlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ModalContainer = styled.div`
  ${tw`bg-white rounded-card max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto`}
`;

const Header = styled.div`
  ${tw`flex items-center justify-between px-6 py-4 border-b border-border-default`}
`;

const HeaderTitle = styled.h2`
  ${tw`text-lg font-semibold text-text-primary`}
`;

const CloseButton = styled.button`
  ${tw`p-2 text-text-secondary`}

  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const Content = styled.div`
  ${tw`p-6`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const Label = styled.label`
  ${tw`block text-sm font-semibold text-text-primary mb-2`}
`;

const CharCount = styled.span<{ isNearLimit?: boolean }>`
  ${tw`text-xs float-right`}
  color: ${props => (props.isNearLimit ? '#dc2626' : 'var(--color-text-secondary)')};
`;

const ReasonInput = styled.input`
  ${tw`w-full px-4 py-3 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent`}
`;

const EvidenceTextarea = styled.textarea`
  ${tw`w-full px-4 py-3 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none`}
  min-height: 120px;
`;

const ReportingUserInfo = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg mb-6`}
`;

const ReportingUserLabel = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const ReportingUserName = styled.p`
  ${tw`text-base font-semibold text-text-primary mt-1`}
`;

const ImagesContainer = styled.div`
  ${tw`flex flex-wrap gap-3`}
`;

const ImageWrapper = styled.div`
  ${tw`relative`}
`;

const SelectedImage = styled.img`
  ${tw`w-20 h-20 rounded-lg object-cover`}
`;

const RemoveImageButton = styled.button`
  ${tw`absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center`}

  &:hover {
    ${tw`bg-red-600`}
  }
`;

const AddImageButton = styled.button`
  ${tw`w-20 h-20 rounded-lg border-2 border-dashed border-border-default flex items-center justify-center text-text-tertiary`}

  &:hover {
    ${tw`border-black text-black`}
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  ${tw`w-full py-4 rounded-card font-semibold text-white`}
  background-color: ${props => (props.disabled ? 'var(--color-border)' : '#dc2626')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  &:hover {
    ${props => !props.disabled && `background-color: #b91c1c;`}
  }
`;

const MAX_REASON_LENGTH = 60;
const MAX_EVIDENCE_LENGTH = 500;
const MAX_IMAGES = 4;

const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  reportedUserId,
  reportedUserName,
  bookingId,
  chatId,
}) => {
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createUserReport] = useMutation(CREATE_USER_REPORT);
  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_REPORT);

  const resetForm = () => {
    setReason('');
    setEvidence('');
    setImages([]);
    setImagePreviewUrls([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddImage = () => {
    if (images.length >= MAX_IMAGES) {
      toast.info(`You can only add up to ${MAX_IMAGES} images.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    const newImages = [...images, ...filesToAdd];
    setImages(newImages);

    // Create preview URLs
    filesToAdd.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreviewUrls(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImageToS3 = async (file: File): Promise<string> => {
    // Get presigned URL from backend
    const { data } = await getPresignedUrl({
      variables: { fileType: file.type || 'image/jpeg' },
    });

    const presignedUrl = data?.getPresignedUrlForReport?.url;
    const key = data?.getPresignedUrlForReport?.key;

    if (!presignedUrl || !key) {
      throw new Error('Failed to get presigned URL');
    }

    // Upload to S3
    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'image/jpeg',
      },
    });

    // Return the S3 URL
    return `https://autopilotee-cars.s3.us-east-2.amazonaws.com/${key}`;
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for your report.');
      return;
    }

    if (!evidence.trim()) {
      toast.error('Please provide detailed evidence for your report.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload images to S3 and get URLs
      const imageUrls: string[] = [];

      for (const file of images) {
        try {
          const s3Url = await uploadImageToS3(file);
          imageUrls.push(s3Url);
        } catch (uploadError) {
          // Continue with other images even if one fails
        }
      }

      await createUserReport({
        variables: {
          input: {
            reportedUserId,
            reason: reason.trim(),
            evidence: evidence.trim(),
            images: imageUrls,
            bookingId,
            chatId,
          },
        },
      });

      toast.success('Report submitted. Thank you for your report. We will review it shortly.');
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit report. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = reason.trim().length > 0 && evidence.trim().length > 0;

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderTitle>Report User</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </Header>

        <Content>
          <ReportingUserInfo>
            <ReportingUserLabel>You are reporting:</ReportingUserLabel>
            <ReportingUserName>{reportedUserName}</ReportingUserName>
          </ReportingUserInfo>

          <Section>
            <Label>
              Reason *
              <CharCount isNearLimit={reason.length >= MAX_REASON_LENGTH - 10}>
                {reason.length}/{MAX_REASON_LENGTH}
              </CharCount>
            </Label>
            <ReasonInput
              type="text"
              placeholder="e.g., Inappropriate behavior, Safety concern..."
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
              maxLength={MAX_REASON_LENGTH}
            />
          </Section>

          <Section>
            <Label>
              Evidence / Details *
              <CharCount isNearLimit={evidence.length >= MAX_EVIDENCE_LENGTH - 50}>
                {evidence.length}/{MAX_EVIDENCE_LENGTH}
              </CharCount>
            </Label>
            <EvidenceTextarea
              placeholder="Please describe the incident in detail. Include dates, times, and any relevant information..."
              value={evidence}
              onChange={e => setEvidence(e.target.value.slice(0, MAX_EVIDENCE_LENGTH))}
              maxLength={MAX_EVIDENCE_LENGTH}
            />
          </Section>

          <Section>
            <Label>Screenshots (Optional, up to {MAX_IMAGES})</Label>
            <ImagesContainer>
              {imagePreviewUrls.map((url, index) => (
                <ImageWrapper key={index}>
                  <SelectedImage src={url} alt={`Screenshot ${index + 1}`} />
                  <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                    <FontAwesomeIcon icon={faTrash} size="xs" />
                  </RemoveImageButton>
                </ImageWrapper>
              ))}
              {images.length < MAX_IMAGES && (
                <AddImageButton onClick={handleAddImage}>
                  <FontAwesomeIcon icon={faPlus} size="lg" />
                </AddImageButton>
              )}
            </ImagesContainer>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Section>

          <SubmitButton onClick={handleSubmit} disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </SubmitButton>
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default ReportUserModal;
